import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, FileText, AlertCircle, CheckCircle2, Info, Download } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ExpenseImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<{ success: boolean; imported: number; skippedVehicles: number; errors: string[] }>;
}

export const ExpenseImportDialog = ({
  isOpen,
  onClose,
  onImport,
}: ExpenseImportDialogProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    imported: number;
    skippedVehicles: number;
    errors: string[];
  } | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files?.[0]) {
      await processFile(files[0]);
    }
  }, []);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.[0]) {
      await processFile(files[0]);
    }
  };

  const processFile = async (file: File) => {
    if (!file.name.endsWith(".csv") && file.type !== "text/csv") {
      setResult({
        success: false,
        imported: 0,
        skippedVehicles: 0,
        errors: ["Please upload a CSV file."],
      });
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const importResult = await onImport(file);
      setResult(importResult);
    } catch (error) {
      setResult({
        success: false,
        imported: 0,
        skippedVehicles: 0,
        errors: [error instanceof Error ? error.message : "An error occurred"],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Expenses from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple expenses at once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Instructions Accordion */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="instructions" className="border rounded-lg">
              <AccordionTrigger className="px-4 py-2 hover:no-underline">
                <div className="flex items-center gap-2 text-sm">
                  <Info className="h-4 w-4 text-primary" />
                  CSV Format Instructions
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-3 text-sm text-muted-foreground">
                  <div>
                    <p className="font-medium text-foreground mb-1">Required Columns:</p>
                    <p>Date, Expense, Expense Type, Amount (or Miles Driven)</p>
                  </div>
                  
                  <div>
                    <p className="font-medium text-foreground mb-1">Optional Columns:</p>
                    <p>Car, Miles Driven (or Amount), Paid By, Paid To, Notes</p>
                  </div>

                  <div>
                    <p className="font-medium text-foreground mb-1">Car Column Matching:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>First matches by <strong>Vehicle Nickname</strong> (exact match)</li>
                      <li>If no nickname match, tries to match by <strong>VIN</strong></li>
                      <li>If no match found, a new vehicle is created with the Car value as its nickname</li>
                    </ul>
                    <p className="mt-2 text-xs italic">
                      Tip: Use your existing vehicle nicknames for best results.
                    </p>
                  </div>

                  <div>
                    <p className="font-medium text-foreground mb-1">Expense Type:</p>
                    <p>If the value doesn't match an existing type, it will default to "Other".</p>
                  </div>

                  <div>
                    <p className="font-medium text-foreground mb-1">Amount & Miles Driven:</p>
                    <p>At least one must be provided. Both can be included if applicable.</p>
                  </div>

                  <div className="pt-2 border-t">
                    <a
                      href="/samples/expenses-sample.csv"
                      download="expenses-sample.csv"
                      className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
                    >
                      <Download className="h-4 w-4" />
                      Download sample CSV template
                    </a>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Upload Zone */}
          {!result && (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={importing}
              />
              <div className="flex flex-col items-center gap-2">
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                    <p className="text-sm text-muted-foreground">Importing expenses...</p>
                  </>
                ) : (
                  <>
                    <Upload className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm font-medium">
                      Drop your CSV file here or click to browse
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Supports CSV files only
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-3">
              {result.success ? (
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <AlertTitle className="text-green-700 dark:text-green-400">
                    Import Successful
                  </AlertTitle>
                   <AlertDescription className="text-green-600 dark:text-green-300">
                    Successfully imported {result.imported} expense{result.imported !== 1 ? "s" : ""}.
                    {result.skippedVehicles > 0 && (
                      <p className="mt-2">
                        {result.skippedVehicles} vehicle{result.skippedVehicles !== 1 ? "s were" : " was"} skipped due to your plan's vehicle limit.{" "}
                        <a href="/app/upgrade" className="underline font-medium">Upgrade your plan</a> to import more.
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Import Failed</AlertTitle>
                  <AlertDescription>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      {result.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                {result.success ? (
                  <Button onClick={handleClose}>Done</Button>
                ) : (
                  <Button variant="outline" onClick={() => setResult(null)}>
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
